import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <article className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-4xl font-bold mb-8 text-white">Privacy Policy</h1>
          
          <p className="text-gray-300 mb-6">
            <strong>Effective Date:</strong> January 1, 2025
          </p>

          <p className="text-gray-300 mb-6">
            Gam Ticket ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">1. Information We Collect</h2>
          
          <h3 className="text-xl font-medium mt-6 mb-3 text-white">a. Personal Information</h3>
          <p className="text-gray-300 mb-4">When you register or use our Service, we may collect:</p>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>Full name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Payment information (processed securely by third-party providers)</li>
          </ul>

          <h3 className="text-xl font-medium mt-6 mb-3 text-white">b. Usage Data</h3>
          <p className="text-gray-300 mb-4">We may automatically collect:</p>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>Device type and operating system</li>
            <li>IP address</li>
            <li>Pages viewed and interactions within the app</li>
            <li>Time and date of access</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">2. How We Use Your Information</h2>
          <p className="text-gray-300 mb-4">We use your information to:</p>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>Provide and maintain the Service</li>
            <li>Process ticket purchases and payments</li>
            <li>Send transactional messages (e.g., ticket confirmations)</li>
            <li>Improve user experience and app functionality</li>
            <li>Respond to customer support inquiries</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">3. Sharing Your Information</h2>
          <p className="text-gray-300 mb-4">We do not sell your personal data. We may share information with:</p>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li><strong>Payment processors:</strong> To complete transactions securely</li>
            <li><strong>Event organizers:</strong> To validate your ticket at entry</li>
            <li><strong>Service providers:</strong> Who help us operate the Service (e.g., cloud hosting, analytics)</li>
            <li><strong>Legal authorities:</strong> When required by law or to protect our rights</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">4. Data Security</h2>
          <p className="text-gray-300 mb-6">
            We implement industry-standard security measures to protect your data. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">5. Data Retention</h2>
          <p className="text-gray-300 mb-6">
            We retain your personal data only as long as necessary to provide the Service and fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">6. Your Rights</h2>
          <p className="text-gray-300 mb-4">Depending on your jurisdiction, you may have the right to:</p>
          <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
            <li>Access the personal data we hold about you</li>
            <li>Request correction or deletion of your data</li>
            <li>Object to or restrict certain processing</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="text-gray-300 mb-6">
            To exercise these rights, contact us at <a href="mailto:privacy@gamticket.com" className="text-[#0055FF] hover:underline">privacy@gamticket.com</a>.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">7. Children's Privacy</h2>
          <p className="text-gray-300 mb-6">
            Our Service is not intended for children under 13. We do not knowingly collect data from children. If you believe we have collected such data, please contact us immediately.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">8. Changes to This Policy</h2>
          <p className="text-gray-300 mb-6">
            We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of the Service after changes constitutes acceptance.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4 text-white">9. Contact Us</h2>
          <p className="text-gray-300 mb-6">
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8">
            <p className="text-white font-semibold mb-2">Gam Ticket</p>
            <p className="text-gray-300">
              Email: <a href="mailto:privacy@gamticket.com" className="text-[#0055FF] hover:underline">privacy@gamticket.com</a>
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Gam Ticket. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
